import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { WhatsAppIcon } from "@/components/WhatsAppButton";
import { TEMPLATE_TYPES, DEFAULT_TEMPLATES, SAMPLE_MEMBER, renderTemplate } from "@/lib/waTemplates";
import { RotateCcw } from "lucide-react";

const TYPES = Object.keys(TEMPLATE_TYPES);

function Preview({ type, text, guestMissing }) {
  const vars = { ...(guestMissing ? {} : SAMPLE_MEMBER), ...TEMPLATE_TYPES[type].sample };
  return (
    <div className="rounded-xl p-3 text-sm whitespace-pre-wrap" style={{ background: "#dcf8c6", color: "#111" }}
         data-testid="wa-template-preview">{renderTemplate(text, vars)}</div>
  );
}

export default function WaTemplatesCard() {
  const [saved, setSaved] = useState({});
  const [form, setForm] = useState(DEFAULT_TEMPLATES);
  const [type, setType] = useState("offer");
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    api.get("/public/wa-templates").then(({ data }) => {
      setSaved(data || {});
      setForm(Object.fromEntries(TYPES.map((k) => [k, data?.[k] || DEFAULT_TEMPLATES[k]])));
      setLoaded(true);
    }).catch((e) => toast.error(formatError(e)));
  }, []);

  const set = (v) => setForm((f) => ({ ...f, [type]: v }));
  const insert = (token) => {
    const el = ref.current, text = form[type];
    const s = el?.selectionStart ?? text.length, e = el?.selectionEnd ?? text.length;
    set(text.slice(0, s) + token + text.slice(e));
    requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(s + token.length, s + token.length); });
  };
  const save = async () => {
    setBusy(true);
    try {
      const body = Object.fromEntries(TYPES.map((k) => [k, form[k] === DEFAULT_TEMPLATES[k] ? (saved[k] ? "" : null) : form[k]]));
      const { data } = await api.put("/admin/wa-templates", body);
      setSaved(data);
      toast.success("Message templates saved");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="w-card w-card-bordered p-6 space-y-4 mt-6" data-testid="wa-templates-card">
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 rounded-full grid place-items-center text-white" style={{ background: "#25D366" }}>
          <WhatsAppIcon className="h-5 w-5" />
        </div>
        <div>
          <h3 className="font-bold">Member WhatsApp messages</h3>
          <p className="text-xs" style={{ color: "var(--text-muted)" }}>
            Pre-filled text sent when a logged-in member enquires from the portal. Guests keep the standard message.
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {TYPES.map((k) => (
          <button key={k} type="button" onClick={() => setType(k)} data-testid={`wa-template-tab-${k}`}
                  className={`h-8 px-3 rounded-full text-xs font-medium ${type === k ? "bg-navy text-white" : ""}`}
                  style={type === k ? {} : { border: "1px solid var(--border)" }}>{TEMPLATE_TYPES[k].label}</button>
        ))}
      </div>
      <Textarea ref={ref} rows={4} maxLength={1000} value={form[type]} onChange={(e) => set(e.target.value)} data-testid="wa-template-input" />
      <div className="flex flex-wrap gap-1.5 items-center text-xs" style={{ color: "var(--text-muted)" }}>
        Insert:
        {TEMPLATE_TYPES[type].vars.map((v) => (
          <button key={v} type="button" onClick={() => insert(`{${v}}`)} data-testid={`wa-template-var-${v}`}
                  className="px-2 py-0.5 rounded-md font-mono" style={{ background: "var(--bg-soft)", border: "1px solid var(--border)" }}>{`{${v}}`}</button>
        ))}
      </div>
      <p className="text-xs" style={{ color: "var(--text-muted)" }}>
        Wrap optional parts in [square brackets]. They are hidden when a value inside is missing, e.g. <code>[ (Membership ID: {"{membership_id}"})]</code>.
      </p>
      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs font-semibold">
          <span>Live preview</span>
          <label className="inline-flex items-center gap-1.5 font-normal cursor-pointer">
            <Switch checked={missing} onCheckedChange={setMissing} data-testid="wa-template-missing-toggle" />
            Preview with missing member details
          </label>
        </div>
        <Preview type={type} text={form[type]} guestMissing={missing} />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="button" onClick={save} disabled={busy || !loaded} className="rounded-xl bg-navy text-white" data-testid="wa-templates-save-btn">
          {busy ? "Saving…" : "Save templates"}
        </Button>
        <Button type="button" variant="outline" className="rounded-xl" onClick={() => set(DEFAULT_TEMPLATES[type])} data-testid="wa-template-reset-btn">
          <RotateCcw className="h-4 w-4 mr-1.5" /> Reset to default
        </Button>
      </div>
    </div>
  );
}
